import { Head, router } from "@inertiajs/react";
import { t } from "@travily/i18n";
import { rootPath } from "@travily/routes";
import { Frame } from "@ui/components/Frame";
import type { ReactNode } from "react";
import { ShowView } from "./show.view";

const KNOWN_STATUSES = ["400", "403", "404", "422", "500", "503"] as const;

type KnownStatus = (typeof KNOWN_STATUSES)[number];

interface StatusConfig {
  primaryAction: { labelKey: string; handler: () => void };
  secondaryAction?: { labelKey: string; handler: () => void };
}

const goBack = () => window.history.back();
// biome-ignore lint/plugin/no-window-location: Error recovery requires full page reload
const retry = () => window.location.reload();
const goToHomepage = () => router.visit(rootPath());

const STATUS_CONFIG: Record<KnownStatus | "unknown", StatusConfig> = {
  "400": {
    primaryAction: { labelKey: "web.errors.show.actions.retry", handler: retry },
    secondaryAction: { labelKey: "web.errors.show.actions.go_to_homepage", handler: goToHomepage },
  },
  "403": {
    primaryAction: { labelKey: "web.errors.show.actions.go_to_homepage", handler: goToHomepage },
  },
  "404": {
    primaryAction: { labelKey: "web.errors.show.actions.go_back", handler: goBack },
    secondaryAction: { labelKey: "web.errors.show.actions.go_to_homepage", handler: goToHomepage },
  },
  "422": {
    primaryAction: { labelKey: "web.errors.show.actions.go_back", handler: goBack },
    secondaryAction: { labelKey: "web.errors.show.actions.go_to_homepage", handler: goToHomepage },
  },
  "500": {
    primaryAction: { labelKey: "web.errors.show.actions.retry", handler: retry },
    secondaryAction: { labelKey: "web.errors.show.actions.go_to_homepage", handler: goToHomepage },
  },
  "503": {
    primaryAction: { labelKey: "web.errors.show.actions.retry", handler: retry },
    secondaryAction: { labelKey: "web.errors.show.actions.go_to_homepage", handler: goToHomepage },
  },
  unknown: {
    primaryAction: { labelKey: "web.errors.show.actions.retry", handler: retry },
    secondaryAction: { labelKey: "web.errors.show.actions.go_to_homepage", handler: goToHomepage },
  },
};

function buildAction(config: { labelKey: string; handler: () => void }) {
  // i18n-tasks-use t('web.errors.show.actions.retry')
  // i18n-tasks-use t('web.errors.show.actions.go_to_homepage')
  // i18n-tasks-use t('web.errors.show.actions.go_back')
  return { label: t(config.labelKey), onPress: config.handler };
}

interface ErrorPageProps {
  status: number;
}

const ErrorPage = ({ status }: ErrorPageProps) => {
  const code = String(status);
  const key = KNOWN_STATUSES.includes(code as KnownStatus) ? (code as KnownStatus) : "unknown";
  const config = STATUS_CONFIG[key];
  // i18n-tasks-use t('web.errors.show.400.title')
  // i18n-tasks-use t('web.errors.show.403.title')
  // i18n-tasks-use t('web.errors.show.404.title')
  // i18n-tasks-use t('web.errors.show.422.title')
  // i18n-tasks-use t('web.errors.show.500.title')
  // i18n-tasks-use t('web.errors.show.503.title')
  // i18n-tasks-use t('web.errors.show.unknown.title')
  const title = t(`web.errors.show.${key}.title`);
  // i18n-tasks-use t('web.errors.show.400.description')
  // i18n-tasks-use t('web.errors.show.403.description')
  // i18n-tasks-use t('web.errors.show.404.description')
  // i18n-tasks-use t('web.errors.show.422.description')
  // i18n-tasks-use t('web.errors.show.500.description')
  // i18n-tasks-use t('web.errors.show.503.description')
  // i18n-tasks-use t('web.errors.show.unknown.description')
  const description = t(`web.errors.show.${key}.description`);

  return (
    <Frame>
      <Frame.Content centred>
        <Head title={`${title} — Travily`}>
          <meta name="description" content={description} />
        </Head>
        <ShowView
          title={title}
          description={description}
          primaryAction={buildAction(config.primaryAction)}
          secondaryAction={config.secondaryAction ? buildAction(config.secondaryAction) : undefined}
        />
      </Frame.Content>
    </Frame>
  );
};

ErrorPage.layout = (page: ReactNode) => page;

export default ErrorPage;
